import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Alert, 
  CircularProgress,
  Card,
  CardContent 
} from '@mui/material';
import { CheckCircle, Visibility, VisibilityOff } from '@mui/icons-material';
import { StyledTextField, StyledButton } from '../../components/host/OnboardingComponents';
import { hostService } from '../../services/hostService';
import styled from 'styled-components';

const RootContainer = styled(Box)`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #181e25;
  padding: 2rem;
`;

const SetupCard = styled(Card)`
  background: #232933;
  border-radius: 24px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.37);
`;

const HostPasswordSetup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const token = searchParams.get('token');
  const userId = searchParams.get('userId');

  useEffect(() => {
    if (!token || !userId) {
      setError('Invalid or missing password setup link. Please contact support.');
    }
  }, [token, userId]);

  const validatePassword = (password: string) => {
    const minLength = password.length >= 8;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    
    return {
      minLength,
      hasUpper,
      hasLower,
      hasNumber,
      isValid: minLength && hasUpper && hasLower && hasNumber
    };
  };

  const passwordValidation = validatePassword(password);
  const passwordsMatch = password === confirmPassword;
  const canSubmit = passwordValidation.isValid && passwordsMatch && password.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!canSubmit || !token || !userId) return;

    setLoading(true);
    setError('');

    try {
      const response = await hostService.setupPassword(token, userId, password);
      
      setSuccess(response.message);
      
      // Store auth token if provided
      if (response.data.token) {
        localStorage.setItem('authToken', response.data.token);
      }

      // Redirect to host dashboard after a short delay
      setTimeout(() => {
        navigate('/host/dashboard');
      }, 2000);

    } catch (error: any) {
      setError(error.message || 'Failed to set up password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token || !userId) {
    return (
      <RootContainer>
        <SetupCard>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ color: '#ef4444', mb: 2 }}>
              Invalid Setup Link
            </Typography>
            <Typography variant="body1" sx={{ color: '#b0b8c1', mb: 3 }}>
              This password setup link is invalid or has expired. Please contact our support team for assistance.
            </Typography>
            <StyledButton 
              variant="primary" 
              onClick={() => window.open('mailto:support@bellhopexpress.com')}
            >
              Contact Support
            </StyledButton>
          </CardContent>
        </SetupCard>
      </RootContainer>
    );
  }

  if (success) {
    return (
      <RootContainer>
        <SetupCard>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: '4rem', color: '#4ade80', mb: 2 }} />
            <Typography variant="h4" sx={{ color: '#fff', mb: 2, fontWeight: 700 }}>
              Welcome to BellHop!
            </Typography>
            <Typography variant="h6" sx={{ color: '#4ade80', mb: 2 }}>
              Password Set Successfully
            </Typography>
            <Typography variant="body1" sx={{ color: '#b0b8c1', mb: 3 }}>
              {success}
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Redirecting you to your dashboard...
            </Typography>
            <CircularProgress sx={{ color: '#377dff', mt: 2 }} />
          </CardContent>
        </SetupCard>
      </RootContainer>
    );
  }

  return (
    <RootContainer>
      <SetupCard>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
              Set Up Your Password
            </Typography>
            <Typography variant="body1" sx={{ color: '#b0b8c1' }}>
              Create a secure password to complete your host account setup
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 3 }}>
              <StyledTextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                InputProps={{
                  endAdornment: (
                    <Box
                      sx={{ cursor: 'pointer', color: '#b0b8c1', ml: 1 }}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </Box>
                  ),
                }}
              />

              {/* Password Requirements */}
              {password && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ color: '#b0b8c1', mb: 1, display: 'block' }}>
                    Password Requirements:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ 
                      color: passwordValidation.minLength ? '#4ade80' : '#ef4444' 
                    }}>
                      {passwordValidation.minLength ? '✓' : '✗'} At least 8 characters
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      color: passwordValidation.hasUpper ? '#4ade80' : '#ef4444' 
                    }}>
                      {passwordValidation.hasUpper ? '✓' : '✗'} One uppercase letter
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      color: passwordValidation.hasLower ? '#4ade80' : '#ef4444' 
                    }}>
                      {passwordValidation.hasLower ? '✓' : '✗'} One lowercase letter
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      color: passwordValidation.hasNumber ? '#4ade80' : '#ef4444' 
                    }}>
                      {passwordValidation.hasNumber ? '✓' : '✗'} One number
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 4 }}>
              <StyledTextField
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                InputProps={{
                  endAdornment: (
                    <Box
                      sx={{ cursor: 'pointer', color: '#b0b8c1', ml: 1 }}
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </Box>
                  ),
                }}
              />

              {/* Password Match Indicator */}
              {confirmPassword && (
                <Typography variant="caption" sx={{ 
                  color: passwordsMatch ? '#4ade80' : '#ef4444',
                  mt: 1,
                  display: 'block'
                }}>
                  {passwordsMatch ? '✓ Passwords match' : '✗ Passwords do not match'}
                </Typography>
              )}
            </Box>

            <StyledButton
              type="submit"
              variant="primary"
              fullWidth
              disabled={!canSubmit || loading}
              sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 600 }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Set Password & Continue'}
            </StyledButton>
          </form>

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Need help?{' '}
              <Typography
                component="span"
                sx={{ color: '#377dff', cursor: 'pointer' }}
                onClick={() => window.open('mailto:support@bellhopexpress.com')}
              >
                Contact Support
              </Typography>
            </Typography>
          </Box>
        </CardContent>
      </SetupCard>
    </RootContainer>
  );
};

export default HostPasswordSetup;
